import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Following the PCs after the person asked one of them for something that changes what it
 * reports (F161): an agent's sign-in there, or its own page opened there. The page that lists the
 * PCs reads them again with its waiting poll while it follows, so the agent's new state shows as
 * soon as the connector reports it, with no reload. Five minutes leave time for a provider's
 * sign-in; each new request starts them again. The poll (useRefreshWhile) is the only clock: its
 * tick also ends the five minutes.
 */
export const FOLLOW_MILLISECONDS = 5 * 60_000;

export interface FollowPc {
  /** Whether the page follows its PCs now. */
  readonly following: boolean;
  /** Starts following, or follows five more minutes. */
  readonly follow: () => void;
}

const FollowPcContext = createContext<FollowPc>({ following: false, follow: () => undefined });

/** Given by a page that lists the PCs; elsewhere, following does nothing. */
export const FollowPcProvider = FollowPcContext.Provider;

export function useFollowPc(): FollowPc {
  return useContext(FollowPcContext);
}

/** For the page that lists the PCs: what it gives its parts, and what each tick of its poll does. */
export function useFollowing(refresh: () => void): { readonly value: FollowPc; readonly tick: () => void } {
  const [until, setUntil] = useState<number | null>(null);
  const follow = useCallback(() => { setUntil(Date.now() + FOLLOW_MILLISECONDS); }, []);
  const tick = useCallback(() => {
    if (until !== null && Date.now() >= until) setUntil(null);
    refresh();
  }, [until, refresh]);
  const value = useMemo(() => ({ following: until !== null, follow }), [until, follow]);
  return { value, tick };
}
